/**
 * Every runtime this chain has run.
 *
 * The one list on the site with no other home: a block does not name its
 * runtime, the standings do not, and until this page existed a runtime was
 * reachable only by guessing a `spec_version`. The heights come from bisecting
 * the upgrade boundaries, so each row is the block that runtime took over at
 * rather than the block this observer happened to notice it.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { RuntimeSummary } from '../api/generated/RuntimeSummary'
import { RequestFailed, fetchRuntimes } from '../api/rest'
import { height as fmtHeight, when } from '../lib/format'
import { href } from '../lib/routes'

export function RuntimesIndex({ chain, current }: { chain: string; current: number | null }) {
  const [rows, setRows] = useState<RuntimeSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setRows(null)
    setError(null)
    fetchRuntimes(chain, controller.signal)
      .then(setRows)
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
    return () => controller.abort()
  }, [chain])

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <h2 className="panel-title">Runtimes</h2>
        <span className="eyebrow">newest first</span>
      </div>

      {error && <p className="empty">{error}</p>}
      {!error && rows === null && <p className="empty">Reading the record…</p>}
      {rows !== null && rows.length === 0 && (
        <p className="empty">
          No metadata cached for this chain yet. It is fetched the first time a block is decoded.
        </p>
      )}

      {rows !== null && rows.length > 0 && (
        <div className="scroll-x">
          <table className="board">
            <caption className="visually-hidden">
              Runtime versions this chain has run, newest first
            </caption>
            <thead>
              <tr>
                <th scope="col">Version</th>
                <th scope="col" className="left">
                  Name
                </th>
                <th scope="col">In force from</th>
                <th scope="col">Metadata</th>
                <th scope="col" className="last-seen">
                  Cached
                </th>
              </tr>
            </thead>
            <tbody>
              {[...rows].reverse().map((r) => (
                <tr key={r.spec_version}>
                  <td className="numeral">
                    <Link to={href({ chain, runtime: r.spec_version })}>v{r.spec_version}</Link>
                  </td>
                  <td className="left">
                    <span className="miner-cell">
                      <code>{r.spec_name}</code>
                      {r.spec_version === current && <span className="chip chip-you">Current</span>}
                    </span>
                  </td>
                  <td className="numeral">
                    <Link to={href({ chain, block: String(r.first_seen_height) })}>
                      #{fmtHeight(r.first_seen_height)}
                    </Link>
                  </td>
                  <td className="numeral">{Math.round(r.bytes / 1024)} KiB</td>
                  <td className="numeral last-seen" style={{ color: 'var(--text-muted)' }}>
                    {when(r.fetched_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="panel-note">
        Found by bisecting the upgrade boundaries — <code>spec_version</code> only increases, so the
        blocks where it changes are a sorted sequence and each is found in a few dozen probes rather
        than by walking the chain. Metadata is cached per version, so a runtime stays readable after
        the node has pruned the state it would need to describe itself again.
      </p>
    </section>
  )
}
